'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'

import { AppInstallAccountsDs } from '@src/components/blocks/app-install-accounts-ds'
import { WishlistShareSectionDS } from '@src/components/blocks/wishlist-share-section-ds'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { TRACK_PAGE_TYPE } from '@src/quince-site-common/src/domains/PageTypes'
import { useAppInstallAccountsPresenter } from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install.presenter'

import type { WishlistHeadingDSProps } from './wishlist-heading-ds.types'

const headingWrapperCss = css({
  mt: 'space-64',
  textAlign: 'center',
  position: 'relative',
})

const headingCss = css({
  textStyle: 'title-24',
  textTransform: 'uppercase',
  mb: 'space-10',
})

const subHeadingCss = css({
  textStyle: 'body-16',
})

// Compact layout (app-install banner present): heading block on one side, banner on the other.
// Horizontal gutters come from the enclosing SectionContentContainer so the row aligns with the grid below.
// Desktop BOTTOM-aligns the QR block with the title and gaps 24px (Figma 5013:45297 `header` =
// items-end, gap-24); mobile stacks and centers.
const wishlistHeadingWrapperCss = css({
  display: 'flex',
  mt: 'space-32',
  flexDirection: { base: 'column', lg: 'row' },
  alignItems: { base: 'center', lg: 'flex-end' },
  justifyContent: { lg: 'space-between' },
  gap: { base: 'space-20', lg: 'space-24' },
})

// Mobile is a single row: title on the left, share on the right, sharing a TEXT BASELINE
// (Figma 6868:6930 `Wrapper`).
//
// Figma expresses that as `items-end` on a 28px-tall title box and a 20px-tall share box, and this
// row reproduces both boxes exactly — but copying `items-end` into CSS does NOT reproduce the look.
// `title-24-dec` puts IvyPresto 24px into a 28px line-height, and the browser's line box seats that
// font's baseline 2px lower than Figma's text engine does. Flushing the box bottoms therefore leaves
// the share label 2px above the title's baseline, which is the misalignment in STRFRNT-16193 (an
// earlier pass moved `center` -> `flex-end` here and closed most, but not all, of the gap).
// `baseline` aligns what the eye actually reads, and stays correct if either textStyle is retuned.
// Costs 2px of row height: the share box now hangs 2px below the title box to seat its baseline.
// Desktop stacks share above title, flush left with a 4px gap (Figma 5013:45299 = flex-col gap-4
// items-start) — a column, where align-items is the horizontal axis, so it keeps `flex-start`.
const headingWrapperCompactCss = css({
  display: 'flex',
  gap: { base: 'space-4', lg: 'space-4' },
  flexDirection: { base: 'row', lg: 'column-reverse' },
  alignItems: { base: 'baseline', lg: 'flex-start' },
  justifyContent: { base: 'space-between', lg: 'flex-start' },
  width: { base: 'full', lg: 'auto' },
})

// Decorative (ivy) title, weight 300: 24px/1.18 mobile (Figma 5013:45016), 32px/1.24 desktop
// (Figma 5013:45304). The desktop size was 28px against an earlier frame (6868:6959); STRFRNT-16992
// pinned 32px as the current spec, so the two breakpoints now cite the same frame family.
const headingCompactCss = css({
  textStyle: { base: 'title-24-dec', lg: 'title-32-dec' },
})

interface WishlistHeadingDSContentProps extends WishlistHeadingDSProps {
  showCompactLayout?: boolean
}

export function WishlistHeadingDS({ name, isSelfWishlist, wishListProductList }: WishlistHeadingDSProps) {
  const { t } = useTranslation()
  const { shouldRender: shouldRenderAppInstallAccounts } = useAppInstallAccountsPresenter({ placement: 'wishlist' })
  if (shouldRenderAppInstallAccounts) {
    return (
      <SectionContentContainer>
        <div className={wishlistHeadingWrapperCss}>
          <WishlistHeadingDSContent
            name={name}
            isSelfWishlist={isSelfWishlist}
            wishListProductList={wishListProductList}
            showCompactLayout
          />
          <AppInstallAccountsDs
            trackPageType={TRACK_PAGE_TYPE.WISHLIST}
            placement="wishlist"
            desktopConfig={{ title: t('get_the_app'), qrAlt: t('app_qr_alt') }}
            mobileConfig={{
              title: t('keep_track_fav_items'),
              actionLabel: t('get_the_app'),
            }}
          />
        </div>
      </SectionContentContainer>
    )
  }
  return (
    <WishlistHeadingDSContent name={name} isSelfWishlist={isSelfWishlist} wishListProductList={wishListProductList} />
  )
}

function WishlistHeadingDSContent({
  name,
  isSelfWishlist,
  wishListProductList,
  showCompactLayout,
}: WishlistHeadingDSContentProps) {
  const { t } = useTranslation('wishlist')
  return (
    <div className={showCompactLayout ? headingWrapperCompactCss : headingWrapperCss}>
      <h1 className={showCompactLayout ? headingCompactCss : headingCss}>
        {name ? t('favorites_possessive', { name: name }) : t('my_favorites')}
      </h1>
      {name ? <div className={subHeadingCss}>{t('heres_whats_on_their_wishlist')}</div> : null}
      {isSelfWishlist ? <WishlistShareSectionDS wishListProductList={wishListProductList} /> : null}
    </div>
  )
}
